// ============================================================================
// Session start / folder change
// ============================================================================
import {
  dirPath, onboardStart, dirBadge, inner, input, sendBtn,
} from "./dom.js";

// Deps populated by initSessionControl()
let getSelectedDir = () => "";
let setSelectedDir = null;
let getWs = () => null;
let getSessionActive = () => false;
let setSessionActive = null;
let getPendingStart = () => null;
let setPendingStart = null;
let getActiveCwd = () => "";
let getBusy = () => false;
let getPendingImages = () => [];
let setPendingImages = null;
let getAvailableModes = () => [];
let getTurboEnabled = () => false;
let setQueueState = null;
let setIntentionalClose = null;
let setLoadingHistory = null;
let setResuming = null;
let setExpectedSessionId = null;
let setUserPinnedAway = null;
let getAgentMsgs = () => new Map();
let getToolCards = () => new Map();

let sendRef = null;
let setActiveCwdRef = null;
let showChatViewRef = null;
let setStatusRef = null;
let renderSidebarRef = null;
let setSessionIdRef = null;
let connectRef = null;
let saveRecentDirRef = null;
let renderQueuedTrayRef = null;
let hideReconnectBannerRef = null;
let userMsgRef = null;
let rememberLiveUserMsgRef = null;
let renderPastePreviewRef = null;
let setBusyRef = null;

export function selectDir(dir) {
  setSelectedDir(dir);
  dirPath.textContent = dir;
  onboardStart.disabled = false;
}

export function doStartSession(cwd, sessionId) {
  if (!sendRef({ type: "start", cwd, sessionId })) {
    setPendingStart({ cwd, sessionId });
    return;
  }
  setSessionActive(false);
  setActiveCwdRef(cwd);
  dirBadge.textContent = cwd;
  dirBadge.title = cwd;
  showChatViewRef();
  // For a new session (no sessionId), clear the chat view but KEEP the
  // sidebar — the session list is account-scoped (all folders), not
  // process-scoped, and the server's sessions_list / sessions_update
  // will refresh it. Clearing it caused an empty-sidebar flash lasting
  // the whole spawn → session/new → session/list round-trip.
  // For a resume (sessionId present), keep cached messages AND the
  // sidebar visible until the server's sessions_list arrives.
  if (!sessionId) {
    inner.innerHTML = "";
    getAgentMsgs().clear();
    getToolCards().clear();
    setLoadingHistory(false);
    setResuming(false);
    setExpectedSessionId(null);
    setSessionIdRef(null);
  } else {
    setLoadingHistory(true);
    setResuming(true);
    setExpectedSessionId(sessionId);
    // Don't clear sessions or sessionId — keep the sidebar populated
    // and currentSessionId available for probeOrReconnect retries.
    // The ready/reattached handler will update both from server truth.
  }
  setUserPinnedAway(false);
  renderSidebarRef();
  setStatusRef("", sessionId ? "resuming…" : "starting…");
}

export function startSession() {
  const dir = getSelectedDir();
  if (!dir) return;
  onboardStart.disabled = true;
  onboardStart.textContent = "Starting…";
  saveRecentDirRef(dir);
  // Connect WS (if not already connected) then start the session.
  // doStartSession will queue the start as pendingStart if WS isn't open yet.
  const ws = getWs();
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    setPendingStart({ cwd: dir, sessionId: null });
    setActiveCwdRef(dir);
    dirBadge.textContent = dir;
    dirBadge.title = dir;
    showChatViewRef();
    setStatusRef("", "starting…");
    connectRef();
  } else {
    doStartSession(dir, null);
  }
  onboardStart.disabled = false;
  onboardStart.textContent = "Start session";
}

// Slash commands that map to session mode changes (sent via set_mode,
// bypassing the prompt queue for instantaneous effect).
export const MODE_COMMAND_ALIASES = {
  "bypass": "bypass", "yolo": "bypass", "dangerous": "bypass",
  "normal": "normal",
  "plan": "plan",
  "accept-edits": "accept-edits",
  "smart": "smart",
  "autonomous": "autonomous",
};

// Short labels (≤6 chars) shown on the mode badge pill. The full mode
// name + description from the CLI is still shown in the picker modal.
export const MODE_SHORT_LABELS = {
  "normal": "std",
  "plan": "plan",
  "accept-edits": "accept",
  "bypass": "bypass",
  "smart": "smart",
  "autonomous": "auto",
};
export function modeShortLabel(modeId) {
  return MODE_SHORT_LABELS[modeId] || modeId.slice(0, 6);
}

// Given a slash command name, return the matching modeId if it's a mode change.
export function resolveModeCommand(cmdName) {
  const lower = cmdName.toLowerCase();
  // Check direct aliases first
  if (MODE_COMMAND_ALIASES[lower]) return MODE_COMMAND_ALIASES[lower];
  // Check if it matches an available mode ID
  for (const m of getAvailableModes()) {
    if (m.id.toLowerCase() === lower) return m.id;
  }
  return null;
}

export function sendPrompt() {
  const text = input.value.trim();
  const pendingImages = getPendingImages();
  if ((!text && pendingImages.length === 0) || !getSessionActive()) return;

  // Check if this is a slash command that maps to a mode change
  if (text.startsWith("/")) {
    const slashCmd = text.slice(1).split(/\s+/)[0]; // first word after /
    // /turbo is a gateway-level toggle, not an ACP mode — sent via
    // set_turbo (queue-bypassing, works mid-turn like set_mode).
    if (slashCmd.toLowerCase() === "turbo") {
      userMsgRef(text, []);
      rememberLiveUserMsgRef(text);
      input.value = "";
      input.style.height = "auto";
      setPendingImages([]);
      renderPastePreviewRef();
      sendRef({ type: "set_turbo", enabled: !getTurboEnabled() });
      return;
    }
    const modeId = resolveModeCommand(slashCmd);
    if (modeId) {
      // Mode change: send instantaneously via set_mode (bypasses the queue)
      userMsgRef(text, []);
      rememberLiveUserMsgRef(text);
      input.value = "";
      input.style.height = "auto";
      setPendingImages([]);
      renderPastePreviewRef();
      sendRef({ type: "set_mode", modeId });
      return;
    }
  }

  // Regular prompt or non-mode slash command — send to the queue
  const wasBusy = getBusy();
  const images = pendingImages.slice();
  // Clear agent message map so the next response creates a fresh bubble
  // instead of appending to the last replayed message from session history.
  getAgentMsgs().clear();
  if (wasBusy) {
    // Already busy — this prompt will be queued server-side. Do NOT
    // optimistically render it into the stream: the server emits the
    // synthetic user_message_chunk at turn-start (not enqueue time), so
    // rendering now would show the message out of order while the current
    // turn is still running. The queue_update frame (fired from the
    // queue's onChange listener) gives immediate feedback in the queued
    // tray. When this prompt's turn starts, the transient echo will
    // render it for all clients (including this one).
  } else {
    // Idle — render optimistically so the sender sees their message
    // instantly. The server's transient echo at turn-start (milliseconds
    // later) is deduped via isLiveUserMsgEcho() so we don't double-render.
    userMsgRef(text, images);
    rememberLiveUserMsgRef(text, images);
    setBusyRef(true);
  }
  input.value = "";
  input.style.height = "auto";
  setPendingImages([]);
  renderPastePreviewRef();
  if (images.length) console.log("[sendPrompt] Sending", images.length, "images, sizes:", images.map(i => i.data.length), "bytes (base64)");
  sendRef({ type: "prompt", text, images: images.length ? images : undefined });
}

// Start a new chat in the same folder. Closes the current WS, reconnects,
// and creates a fresh session (no sessionId) in activeCwd.
export function newSession() {
  const cwd = getActiveCwd();
  if (!cwd) return;
  setPendingStart({ cwd, sessionId: null });
  inner.innerHTML = "";
  getAgentMsgs().clear();
  getToolCards().clear();
  // Keep `sessions` — the sidebar list survives across the reconnect and
  // is refreshed by sessions_list / sessions_update from the server.
  setSessionIdRef(null);
  setSessionActive(false);
  setUserPinnedAway(false);
  setQueueState({ running: null, queued: [] });
  renderQueuedTrayRef();
  hideReconnectBannerRef();
  setStatusRef("", "starting new session…");
  input.disabled = true;
  sendBtn.disabled = true;
  setIntentionalClose(true);
  const ws = getWs();
  if (ws) { try { ws.close(); } catch {} }
  connectRef();
}

// Change folder: close the current WS, reconnect, and start a new session
// in the chosen directory. Called when the user clicks the dir-badge and
// picks a new folder from the modal.
export function changeFolder(newCwd) {
  saveRecentDirRef(newCwd);
  // Queue the start for when the new WS opens
  setPendingStart({ cwd: newCwd, sessionId: null });
  // Clear the chat view for the new session (sidebar list is kept —
  // it's account-scoped, not process-scoped).
  inner.innerHTML = "";
  getAgentMsgs().clear();
  getToolCards().clear();
  setSessionIdRef(null);
  setSessionActive(false);
  setUserPinnedAway(false);
  setQueueState({ running: null, queued: [] });
  renderQueuedTrayRef();
  setActiveCwdRef(newCwd);
  dirBadge.textContent = newCwd;
  dirBadge.title = newCwd;
  setStatusRef("", "starting…");
  hideReconnectBannerRef();
  // Close the current WS — onclose won't show onboarding because
  // sessionActive is already false. We reconnect immediately.
  setIntentionalClose(true);
  const ws = getWs();
  if (ws) { try { ws.close(); } catch {} }
  connectRef();
}

/**
 * Initialize the session control module.
 * @param {object} deps
 */
export function initSessionControl(deps) {
  getSelectedDir = deps.getSelectedDir;
  setSelectedDir = deps.setSelectedDir;
  getWs = deps.getWs;
  getSessionActive = deps.getSessionActive;
  setSessionActive = deps.setSessionActive;
  getPendingStart = deps.getPendingStart;
  setPendingStart = deps.setPendingStart;
  getActiveCwd = deps.getActiveCwd;
  getBusy = deps.getBusy;
  getPendingImages = deps.getPendingImages;
  setPendingImages = deps.setPendingImages;
  getAvailableModes = deps.getAvailableModes;
  getTurboEnabled = deps.getTurboEnabled || (() => false);
  setQueueState = deps.setQueueState;
  setIntentionalClose = deps.setIntentionalClose;
  setLoadingHistory = deps.setLoadingHistory;
  setResuming = deps.setResuming;
  setExpectedSessionId = deps.setExpectedSessionId;
  setUserPinnedAway = deps.setUserPinnedAway;
  getAgentMsgs = deps.getAgentMsgs;
  getToolCards = deps.getToolCards;

  sendRef = deps.send;
  setActiveCwdRef = deps.setActiveCwd;
  showChatViewRef = deps.showChatView;
  setStatusRef = deps.setStatus;
  renderSidebarRef = deps.renderSidebar;
  setSessionIdRef = deps.setSessionId;
  connectRef = deps.connect;
  saveRecentDirRef = deps.saveRecentDir;
  renderQueuedTrayRef = deps.renderQueuedTray;
  hideReconnectBannerRef = deps.hideReconnectBanner;
  userMsgRef = deps.userMsg;
  rememberLiveUserMsgRef = deps.rememberLiveUserMsg;
  renderPastePreviewRef = deps.renderPastePreview;
  setBusyRef = deps.setBusy;
}
